import { Component } from '@theme/component';

/**
 * Device-local wishlist.
 *
 * The list is stored as product HANDLES only — never titles, prices or images.
 * Everything shown to the shopper is fetched fresh from the storefront at render
 * time, so a saved item can never display a stale price or claim to be in stock
 * when it is not.
 *
 * There is no account behind this. The list lives in localStorage on one device
 * and does not follow the shopper to another. That is a deliberate v1 choice:
 * Shopify's new customer accounts cannot be written to from a theme, and gating
 * "save this" behind a sign-in costs more saves than it earns.
 */

const STORAGE_KEY = 'm91:wishlist';
const CHANGE_EVENT = 'wishlist:change';

/**
 * localStorage throws in Safari private mode and when storage is full, and the
 * stored value can be corrupted by anything else on the origin. Every read and
 * write is therefore guarded — a broken store degrades to an empty wishlist
 * rather than taking the page down with it.
 */
const storage = {
  /** @returns {string[]} */
  read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string' && h) : [];
    } catch (_) {
      return [];
    }
  },

  /** @param {string[]} handles */
  write(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (_) {
      // Saving is best-effort. The in-page state still updates so the click is
      // not silently ignored; it just will not survive a reload.
    }
  },
};

export const wishlist = {
  /** @returns {string[]} */
  all() {
    return storage.read();
  },

  /** @param {string} handle */
  has(handle) {
    return storage.read().includes(handle);
  },

  count() {
    return storage.read().length;
  },

  /**
   * @param {string} handle
   * @returns {boolean} the handle's saved state after the toggle
   */
  toggle(handle) {
    const handles = storage.read();
    const index = handles.indexOf(handle);
    const saved = index === -1;

    // Newest first — the wishlist page renders in this order, and a shopper
    // expects what they just saved to be at the top.
    if (saved) handles.unshift(handle);
    else handles.splice(index, 1);

    storage.write(handles);
    broadcast(handles);
    return saved;
  },

  /** @param {string} handle */
  remove(handle) {
    const handles = storage.read().filter((h) => h !== handle);
    storage.write(handles);
    broadcast(handles);
  },
};

/** @param {string[]} handles */
function broadcast(handles) {
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles } }));
}

/**
 * A heart that saves the product it belongs to.
 *
 * Used on product cards and on the product page from the same snippet, so the
 * two surfaces cannot drift apart.
 */
class WishlistButton extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.button = this.querySelector('button');
    this.handle = this.getAttribute('handle');

    this.button?.addEventListener('click', this.onClick);
    document.addEventListener(CHANGE_EVENT, this.sync);

    this.sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.button?.removeEventListener('click', this.onClick);
    document.removeEventListener(CHANGE_EVENT, this.sync);
  }

  /** @param {Event} event */
  onClick = (event) => {
    // The card's heart sits inside the card's link. Without this, saving an
    // item also navigates to it.
    event.preventDefault();
    event.stopPropagation();

    if (!this.handle) return;

    const saved = wishlist.toggle(this.handle);

    // Only the button that was actually pressed animates. The sync below will
    // update every other copy of this heart on the page without animating them.
    if (saved) {
      this.button?.classList.remove('wishlist-button--pop');
      // Force a reflow so the class can be re-applied and replay the animation
      // on a second save.
      void this.button?.offsetWidth;
      this.button?.classList.add('wishlist-button--pop');
    }
  };

  /**
   * Reflects stored state onto the button. Runs on connect — which matters for
   * cards injected after page load, such as the wishlist page itself — and on
   * every change, so a heart on a card and the same heart on the product page
   * never disagree.
   */
  sync = () => {
    if (!this.button || !this.handle) return;

    const saved = wishlist.has(this.handle);
    this.button.setAttribute('aria-pressed', String(saved));

    const label = saved ? this.getAttribute('label-saved') : this.getAttribute('label-save');
    if (label) {
      this.button.setAttribute('aria-label', label);
      const text = this.querySelector('[data-wishlist-text]');
      if (text) text.textContent = label;
    }
  };
}

/**
 * The header count. Hidden entirely at zero rather than showing a "0", which
 * reads as a broken badge.
 */
class WishlistCount extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CHANGE_EVENT, this.sync);
    this.sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener(CHANGE_EVENT, this.sync);
  }

  sync = () => {
    const count = wishlist.count();
    this.textContent = count > 99 ? '99+' : String(count);
    this.toggleAttribute('hidden', count === 0);
  };
}

/**
 * The wishlist page.
 *
 * The server cannot render this — the list is on the device — so the page ships
 * skeletons and fills them in here. Each saved handle is resolved through the
 * Section Rendering API (`/products/<handle>?section_id=wishlist-card`), which
 * means price and availability come from the storefront at request time rather
 * than from anything cached alongside the handle.
 */
class WishlistPage extends Component {
  requiredRefs = ['list', 'empty', 'loading', 'error'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CHANGE_EVENT, this.onChange);
    this.addEventListener('click', this.onClick);
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener(CHANGE_EVENT, this.onChange);
    this.removeEventListener('click', this.onClick);
  }

  /**
   * Removal is handled here rather than per-card: cards are injected as HTML and
   * would otherwise each need their own listener rebound on every render.
   *
   * @param {Event} event
   */
  onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-wishlist-remove]');
    if (!button) return;

    const handle = button.getAttribute('data-wishlist-remove');
    if (handle) wishlist.remove(handle);
  };

  /**
   * Removing an item drops its card without refetching the others — a full
   * re-render would flash the whole grid to remove one tile.
   */
  onChange = () => {
    const handles = wishlist.all();

    for (const card of this.refs.list.querySelectorAll('[data-handle]')) {
      if (!handles.includes(card.getAttribute('data-handle') ?? '')) card.remove();
    }

    // A card saved on another tab will not be in the DOM yet.
    if (handles.length !== this.refs.list.children.length) this.render();
    else if (handles.length === 0) this.show('empty');
  };

  /** @param {'list'|'empty'|'loading'|'error'} state */
  show(state) {
    for (const name of ['list', 'empty', 'loading', 'error']) {
      this.refs[name]?.toggleAttribute('hidden', name !== state);
    }
  }

  async render() {
    const handles = wishlist.all();

    if (handles.length === 0) {
      this.show('empty');
      return;
    }

    this.show('loading');

    const results = await Promise.all(handles.map((handle) => this.fetchCard(handle)));

    // A saved product that has since been deleted, unpublished or renamed comes
    // back empty. Prune it rather than leaving a permanent gap in the grid.
    const missing = handles.filter((handle, index) => results[index] === null);
    for (const handle of missing) wishlist.remove(handle);

    const html = results.filter((markup) => markup !== null).join('');

    if (!html) {
      this.show(missing.length === handles.length && missing.length > 0 ? 'empty' : 'error');
      return;
    }

    this.refs.list.innerHTML = html;
    this.show('list');
  }

  /**
   * @param {string} handle
   * @returns {Promise<string | null>} the card markup, or null if it no longer resolves
   */
  async fetchCard(handle) {
    try {
      const response = await fetch(
        `${window.Shopify?.routes?.root ?? '/'}products/${encodeURIComponent(handle)}?section_id=wishlist-card`
      );
      if (!response.ok) return null;

      const markup = (await response.text()).trim();
      return markup || null;
    } catch (_) {
      return null;
    }
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}
